"use client";

import { useSearchParams, useParams } from "next/navigation";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getVideo } from "@/actions/videos";
import { getMySuggestedUsers } from "@/actions/profiles";
import VideoFeedWithTabs from "@/components/customs/MainContent/VideoFeedWithTabs";
import SuggestedUsers from "@/components/customs/MainContent/SuggestedUsers";
import { getCookieByName, getSessionApiKeySync } from "@/lib/cookies";
import { AUTH_SESSION_CHANGED } from "@/lib/authSession";
import { type SupportedLanguage } from "@/lib/shared";
import { resolveLocale } from "@/lib/resolvedLocale";
import lang from "@/app/[locale]/(main)/lang.json";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { HomeFeedSkeleton } from "@/components/shell/PageSkeletons";

export default function MainHomePage() {
  const params = useParams();
  const search = useSearchParams();
  const location = useLocation();
  const locale = resolveLocale(params.locale as string | undefined);
  const activeTab = search.get("filter") || "All";

  /** يجب أن يطابق الهيدر و`api`: كوكي ثم localStorage (انظر `getSessionApiKeySync`). */
  const [token, setToken] = useState<string | undefined>(() =>
    typeof document !== "undefined"
      ? (getSessionApiKeySync() ?? undefined)
      : undefined,
  );
  const [id, setId] = useState<string | undefined>();

  const refreshSession = useCallback(() => {
    setToken(getSessionApiKeySync() ?? undefined);
  }, []);

  useLayoutEffect(() => {
    refreshSession();
  }, [location.pathname, location.key, refreshSession]);

  useEffect(() => {
    const onAuth = () => refreshSession();
    window.addEventListener(AUTH_SESSION_CHANGED, onAuth);
    return () => window.removeEventListener(AUTH_SESSION_CHANGED, onAuth);
  }, [refreshSession]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const userId = await getCookieByName("id");
      if (!cancelled) setId(userId ?? undefined);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: randomVideo, isPending: videoPending } = useQuery({
    queryKey: ["randomVideo", activeTab],
    queryFn: () => getVideo({ filter: activeTab }),
  });

  const { data: suggestedUsers } = useQuery({
    queryKey: ["suggestedUsers", locale],
    queryFn: () => getMySuggestedUsers(3),
  });

  if (videoPending || !randomVideo) {
    if (videoPending) {
      return <HomeFeedSkeleton />;
    }
    return (
      <div className="w-full min-h-[calc(100vh-65px)] flex items-center justify-center text-muted-foreground">
        {lang["no-video-available"]?.[locale] ?? "…"}
      </div>
    );
  }

  return (
    <div
      className="mx-auto grid min-h-0 w-full max-w-screen grid-cols-1 grid-rows-1 place-items-center gap-6 px-4 lg:grid-cols-2 lg:w-auto lg:max-w-7xl
        max-lg:flex max-lg:min-h-0 max-lg:flex-1 max-lg:max-w-none max-lg:gap-0 max-lg:px-0 max-lg:pb-0
        max-lg:h-[calc(100svh-65px)]
        max-lg:max-h-[calc(100svh-65px)]
        lg:h-[calc(100vh-104px)]"
    >
      {/* موبايل: يملأ ارتفاع المنطقة تحت الهيدر؛ lg: كما كان */}
      <div className="relative col-span-2 h-full min-h-0 w-full max-lg:flex-1 max-lg:pb-0 lg:col-span-1 lg:flex lg:justify-end">
        <div
          className="h-full w-full min-h-0 relative max-lg:max-h-full max-lg:pb-0
            lg:h-[min(100%,calc(100vh-104px))] lg:max-h-[calc(100vh-104px)] lg:w-auto lg:aspect-[9/16] lg:shrink-0 lg:ms-auto"
        >
          {randomVideo.status === "success" && randomVideo.metaData?.id ? (
            <VideoFeedWithTabs
              initialTab={activeTab as "Personal" | "All" | "Job"}
              initialVideo={randomVideo.metaData}
              isLoggedIn={token ? true : false}
              myId={id}
              locale={locale}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {lang["no-video-available"][locale]}
            </div>
          )}
        </div>
      </div>
      <div className="hidden lg:block py-4 h-full w-[380px]">
        {suggestedUsers?.status === "success" && (
          <SuggestedUsers users={suggestedUsers.metaData} locale={locale} />
        )}
      </div>
    </div>
  );
}
